import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import Layout from 'utils/layout';
import { slugify } from 'utils/helpers';

const Swatches = ({ product, currentVariant, handleVariantChange, inputPrefix = '' }) => {
  const layout = Layout.getInstance();
  const [toggled, setToggled] = useState(false);
  const [swatchLimit, setSwatchLimit] = useState(5);

  useEffect(() => {
    setSwatchLimit(!layout.isAboveBreakpoint('lg') ? 5 : 10);
    const resizeHandle = () => {
      setSwatchLimit(!layout.isAboveBreakpoint('lg') ? 5 : 10);
    };
    window.addEventListener('bpChange', resizeHandle);
    return () => {
      window.removeEventListener('bpChange', resizeHandle);
    };
  }, [toggled]);

  const productOptions =
    product?.optionsWithValues !== undefined
      ? product?.optionsWithValues
      : product?.variantBySelectedOptions?.selectedOptions;

  const colorOptionIndex = productOptions?.findIndex((o) => o.name.toLowerCase().includes('color'));
  if (colorOptionIndex < 0) return false;

  const option = useMemo(() => productOptions[colorOptionIndex], [productOptions, colorOptionIndex]);

  const selectedOption = useMemo(
    () => currentVariant?.selectedOptions.find((o) => o.name === option.name),
    [currentVariant, option],
  );

  const updateCurrentVariant = (e) => {
    const updatedOptions = currentVariant.selectedOptions.map((o) => {
      if (o.name === option.name) {
        return { name: o.name, value: e.target.value };
      }
      return o;
    });
    let updatedVariant = product.variants.find(
      (v) => JSON.stringify(v.selectedOptions) === JSON.stringify(updatedOptions),
    );
    if (!updatedVariant) {
      updatedVariant = product.variants.find((v) =>
        v.selectedOptions.some((o) => o.value === e.target.value && o.name.toLowerCase() === 'color'),
      );
    }
    if (updatedVariant) handleVariantChange(updatedVariant.id);
  };
  return (
    <div>
      <div className="mb-1 mt-2 flex flex-wrap">
        <div className="flex flex-1 flex-wrap">
          {option.values.map((value, index) => {
            const isOOS = product.variants.every(({ available }) => !available);
            const swatchClass = window.availableColors?.find((swatch) => slugify(swatch.name) === slugify(value))
              ? `swatch--${slugify(value)}`
              : 'swatch--fallback';
            return (
              <div
                key={`${option.name}-${value}`}
                className={clsx('mr-2.5 flex items-center', {
                  hidden: !toggled && index + 1 > swatchLimit,
                })}
              >
                <label
                  htmlFor={`${product.id}-${inputPrefix}-${slugify(value)}`}
                  className={`swatch relative flex size-5 cursor-pointer items-center justify-center ${swatchClass}`}
                >
                  <input
                    type="radio"
                    id={`${product.id}-${inputPrefix}-${slugify(value)}`}
                    name={`${product.id}-${inputPrefix}-${option.name}`}
                    value={value}
                    checked={selectedOption?.value === value}
                    onChange={updateCurrentVariant}
                    className={clsx(
                      'peer size-5 cursor-pointer border-0 !shadow-none !ring-0 checked:size-[0.875rem] checked:bg-none focus:!ring-0 focus:ring-transparent',
                      {
                        unavailable: isOOS,
                      },
                    )}
                  />

                  <div
                    className={clsx(
                      'swatch__outline transition-[color, opacity] absolute inset-0 rounded-full border opacity-0 duration-200 peer-checked:opacity-100',
                      { '!border-black hover:!border-brand-tangerine': selectedOption?.value === value && !isOOS },
                      { '!border-grey-dark': selectedOption?.value === value && isOOS },
                    )}
                  ></div>
                  <span className={`sr-only`}>{value}</span>
                  {isOOS && <div className="absolute -top-0.5 left-[9px] h-6 w-0.5 rotate-45 bg-grey-dark"></div>}
                </label>
              </div>
            );
          })}

          {option.values.length > swatchLimit && !toggled && (
            <button
              className="body-2-underline flex size-5 items-center justify-start text-left"
              onClick={() => setToggled(true)}
            >
              +{option.values.length - swatchLimit}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Swatches;
